// Narrative content for ten-ish.
// The premise: reality is a Ledger that runs on arithmetic. Something divided by
// zero, the books stopped balancing, and the machines that kept the count turned
// on the digits they were built to serve. The player is a hand of numbers that
// refused to be erased. Theme is cosmic / accounting / math. No em-dashes.

export interface StoryBeat {
  title: string;
  body: string;
  continueLabel: string;
  accent?: number; // panel accent color
}

export const PROLOGUE: StoryBeat = {
  title: 'THE LEDGER BROKE',
  body:
    'Reality kept its books in one great Ledger. Every number had a column. Every sum had to balance.\n\n' +
    'Then something divided by zero.\n\n' +
    'The columns spilled. Operations went feral. The old machines that kept the count turned on the very digits they were built to serve.\n\n' +
    'You are a hand of numbers that refused to be erased. Climb the broken Ledger. Balance what you can. Reach the Equation before it reaches you.',
  continueLabel: 'ACT 1 - THE COUNTING HOUSE      [ BEGIN ]',
  accent: 0xffd166,
};

export const ACT2_INTRO: StoryBeat = {
  title: 'ACT 2 - THE MARGIN',
  body:
    'The Counting House is quiet now. The Abacus has stopped sliding.\n\n' +
    'Below the last ruled line is the Margin, the place where unfinished math is left to rot.\n\n' +
    'Something down here is still trying to solve itself. It has been waiting a long time for a value like you.',
  continueLabel: '[ DESCEND ]',
  accent: 0x9b7bd6,
};

export const ENDING: StoryBeat = {
  title: 'BALANCED',
  body:
    'The Equation resolves. For one clean moment both sides are equal, and you are still standing on the correct side of the sign.\n\n' +
    'The Ledger does not thank you. Ledgers never do. But the columns settle. The Tally takes a breath. Somewhere a zero remembers how to be nothing again.\n\n' +
    'You were ten-ish. It turned out to be enough.',
  continueLabel: '[ SEE YOUR RECORD ]',
  accent: 0xffd166,
};

// Shown over the boss arena before the fight starts.
export interface BossIntro {
  name: string;
  epithet: string;
  line: string;
}

export const BOSS_INTROS: Record<'abacus' | 'equation', BossIntro> = {
  abacus: {
    name: 'THE ABACUS',
    epithet: 'Keeper of the Old Count',
    line: 'You are an unbalanced row. Hold still. I will carry you back to zero.',
  },
  equation: {
    name: 'THE EQUATION',
    epithet: 'The Unsolved Thing in the Margin',
    line: 'Every value resolves. Even you. Let us simplify.',
  },
};

// A short flavor line shown on the defeat screen, varied by how far you reached.
export function getDefeatLine(bossesDefeated: number): string {
  if (bossesDefeated >= 1) {
    return 'The Margin closes over you. So close to balanced. The Ledger rounds you down and the count goes on.';
  }
  return 'The column closes over you. The Ledger rounds you down. Somewhere, the count goes on without your value.';
}
